/**
 * Formatting utilities
 * 
 * Common formatting functions for currency, dates, etc.
 */

/**
 * Format a number as currency with thousands separator
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (e.g., 'USD', 'ZMW')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string (e.g., "ZMW 1,000.00")
 */
export const formatCurrency = (amount, currency = 'USD', decimals = 2) => {
  try {
    const num = parseFloat(amount) || 0;
    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${currency} ${formatted}`;
  } catch (error) {
    return `${currency} 0.00`;
  }
};

/**
 * Format a number with thousands separator (no currency)
 * @param {number} amount - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number string (e.g., "1,000.00")
 */
export const formatNumber = (amount, decimals = 2) => {
  try {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } catch (error) {
    return '0.00';
  }
};

/**
 * Format a date string to local date format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format a date string to local date and time format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date-time string
 */
export const formatDateTime = (dateString) => {
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return 'N/A';
  }
};

export default {
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateTime
};
